import { Facet } from '@coveo/headless';
import {
  DistanceOption,
  LocationSearchFacetControllers,
  LocationSearchFacetKey,
  SearchLocationFields,
  SearchLocationFilterField,
} from './LocationSearch.types';
import { SitesHeadlessFacets } from 'lib/utils/coveo/sitesHeadless/sitesHeadlessFacets';

/**
 * Sitecore Multilist fields may arrive as a raw item array, or wrapped as
 * `{ value }`, `{ jsonValue }`, or `{ targetItems }` depending on Layout Service vs GraphQL.
 */
export const toSitecoreItemList = <T>(field: unknown): T[] => {
  if (Array.isArray(field)) {
    return field as T[];
  }

  if (!field || typeof field !== 'object') {
    return [];
  }

  const wrapped = field as { value?: unknown; jsonValue?: unknown; targetItems?: unknown };
  if (Array.isArray(wrapped.value)) {
    return wrapped.value as T[];
  }
  if (Array.isArray(wrapped.jsonValue)) {
    return wrapped.jsonValue as T[];
  }
  if (Array.isArray(wrapped.targetItems)) {
    return wrapped.targetItems as T[];
  }

  return [];
};

export const LOCATION_SEARCH_FILTER_KEYS = {
  DISTANCE: 'distance',
  CONDITION: 'condition',
  STATUS: 'status',
  AGE_GROUP: 'ageGroup',
  LANGUAGE: 'language',
} as const;

export type LocationSearchFilterKey =
  (typeof LOCATION_SEARCH_FILTER_KEYS)[keyof typeof LOCATION_SEARCH_FILTER_KEYS];

const FACET_LABEL_ALIASES: Record<LocationSearchFacetKey, string[]> = {
  condition: ['condition'],
  status: ['status'],
  ageGroup: ['adults', 'age group', 'agegroup'],
  language: ['language'],
};

export const resolveFilterLabel = (item: SearchLocationFilterField): string =>
  item.fields?.distanceFilterLabel?.value?.trim() ||
  item.fields?.filterLabel?.value?.trim() ||
  item.displayName?.trim() ||
  item.name?.trim() ||
  '';

export const isFilterVisible = (item: SearchLocationFilterField): boolean =>
  Boolean(resolveFilterLabel(item));

export const resolveDistanceOptionLabel = (option: DistanceOption): string =>
  option.fields?.label?.value?.trim() ||
  option.fields?.distanceFilterValue?.value?.trim() ||
  option.fields?.value?.value?.trim() ||
  option.displayName?.trim() ||
  option.name?.trim() ||
  '';

/** Parses "25 miles" / "25 mi" / "Up to 25 miles" → 25; "Any Distance" → undefined (no radius). */
export const parseDistanceMiles = (label: string | null | undefined): number | undefined => {
  if (!label?.trim()) {
    return undefined;
  }

  const normalized = label.trim().toLowerCase();
  if (normalized.includes('any')) {
    return undefined;
  }

  const match = normalized.match(/(\d+(?:\.\d+)?)/);
  if (!match) {
    return undefined;
  }

  const miles = Number(match[1]);
  return Number.isFinite(miles) ? miles : undefined;
};

export interface DistanceFilterMenuOption {
  id: string;
  label: string;
  /** Radius in miles from Sitecore maxDistance; undefined for "Any distance". */
  miles?: number;
}

const resolveDistanceOptionMiles = (option: DistanceOption, label: string): number | undefined => {
  const maxDistance = option.fields?.maxDistance?.value;
  if (typeof maxDistance === 'number' && Number.isFinite(maxDistance)) {
    return maxDistance;
  }
  return parseDistanceMiles(label);
};

/**
 * Maps a Sitecore distance options list into radio menu options.
 * Prefer root `fields.defaultDistance` from SearchLocation; `distanceFilterItems` is a fallback.
 */
export const getDistanceFilterOptions = (rawItems: unknown): DistanceFilterMenuOption[] =>
  toSitecoreItemList<DistanceOption>(rawItems).reduce<DistanceFilterMenuOption[]>(
    (acc, option, index) => {
      const label = resolveDistanceOptionLabel(option);
      if (!label) {
        return acc;
      }

      acc.push({
        id: option.id || `distance-${index}`,
        label,
        miles: resolveDistanceOptionMiles(option, label),
      });
      return acc;
    },
    []
  );

/** Resolves distance options from SearchLocation root fields, then the Distance filter item. */
export const resolveDistanceFilterItemsSource = (
  rootFields: Pick<SearchLocationFields, 'defaultDistance' | 'distanceFilterItems'> | undefined,
  distanceFilterItem: SearchLocationFilterField | undefined
): unknown =>
  rootFields?.defaultDistance ??
  rootFields?.distanceFilterItems ??
  distanceFilterItem?.distanceFilterItems ??
  distanceFilterItem?.fields?.distanceFilterItems;

/** Shortens "25 miles" → "25 mi" for the active pill label. */
export const formatDistancePillValue = (label: string): string => {
  const miles = parseDistanceMiles(label);
  if (miles == null) {
    return label.trim();
  }
  return `${miles} mi`;
};

export const resolveFilterKey = (
  item: SearchLocationFilterField
): LocationSearchFilterKey | null => {
  const label = resolveFilterLabel(item).toLowerCase();
  const name = (item.name || item.displayName || '').trim().toLowerCase();

  if (
    label === 'distance' ||
    name === 'distance' ||
    Boolean(item.fields?.distanceFilterLabel?.value?.trim())
  ) {
    return LOCATION_SEARCH_FILTER_KEYS.DISTANCE;
  }

  const candidates = [label, name].filter(Boolean);

  for (const [facetKey, aliases] of Object.entries(FACET_LABEL_ALIASES) as [
    LocationSearchFacetKey,
    string[]
  ][]) {
    if (candidates.some((candidate) => aliases.includes(candidate))) {
      return facetKey;
    }
  }

  return null;
};

/** Keeps Sitecore order for non-distance filters, but always places Distance first. */
export const sortFiltersWithDistanceFirst = (
  items: SearchLocationFilterField[]
): SearchLocationFilterField[] => {
  const distanceFilters: SearchLocationFilterField[] = [];
  const otherFilters: SearchLocationFilterField[] = [];

  items.forEach((item) => {
    if (resolveFilterKey(item) === LOCATION_SEARCH_FILTER_KEYS.DISTANCE) {
      distanceFilters.push(item);
    } else {
      otherFilters.push(item);
    }
  });

  return [...distanceFilters, ...otherFilters];
};

export const mapSitesFacetsToControllers = (
  facets: SitesHeadlessFacets
): LocationSearchFacetControllers => ({
  condition: facets.siteRelatedConditionsFacet,
  status: facets.siteStatusFacet,
  ageGroup: facets.siteRecruitingTypeFacet,
  language: facets.siteLanguagesFacet,
});

export const getFacetController = (
  controllers: LocationSearchFacetControllers | undefined,
  key: LocationSearchFacetKey
): Facet | undefined => controllers?.[key];

export const getSelectedFacetValues = (facet: Facet | undefined): string[] => {
  if (!facet) {
    return [];
  }
  return facet.state.values
    .filter((value) => facet.isValueSelected(value))
    .map((value) => value.value);
};

export const buildStagedSelectionFromFacet = (
  facet: Facet | undefined
): Record<string, boolean> => {
  if (!facet) {
    return {};
  }

  return facet.state.values.reduce<Record<string, boolean>>((acc, value) => {
    acc[value.value] = facet.isValueSelected(value);
    return acc;
  }, {});
};
